import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { productsAPI, pricesAPI, productImagesAPI } from '../../services/api';
import Button from './Button';
import EditProductDialog from './EditProductDialog';
import EditSizeDialog from './EditSizeDialog';
import AddSizeDialog from './AddSizeDialog';
import { X, Edit, Trash2, Plus, Package } from 'lucide-react';
import { formatCurrency } from '../../common/utils/formatters';
import toast from 'react-hot-toast';
import './ProductDetailsDialog.css';

const ProductDetailsDialog = ({ isOpen, onClose, productId }) => {
  const queryClient = useQueryClient();
  const [editProductOpen, setEditProductOpen] = useState(false);
  const [editSizeOpen, setEditSizeOpen] = useState(false);
  const [addSizeOpen, setAddSizeOpen] = useState(false);
  const [editingSizeId, setEditingSizeId] = useState(null);

  // Fetch product details
  const { data: product, isLoading: loadingProduct } = useQuery(
    ['product', productId],
    async () => {
      const response = await productsAPI.getById(productId);
      return response.data.data;
    },
    { enabled: isOpen && Boolean(productId) }
  );

  // Fetch prices
  const { data: prices, isLoading: loadingPrices } = useQuery(
    ['prices', productId],
    async () => {
      const response = await pricesAPI.list(productId);
      return response.data.data;
    },
    { enabled: isOpen && Boolean(productId) }
  );

  // Fetch product images
  const { data: productImages } = useQuery(
    ['productImages', productId],
    async () => {
      const response = await productImagesAPI.listByProduct(productId);
      return response.data.data;
    },
    { enabled: isOpen && Boolean(productId) }
  );

  const deleteProductMutation = useMutation(
    (id) => productsAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('Product deleted successfully');
        onClose();
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete product');
      },
    }
  );

  const deleteSizeMutation = useMutation(
    (priceId) => pricesAPI.delete(priceId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prices');
        queryClient.invalidateQueries('products');
        toast.success('Size removed successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to remove size');
      },
    }
  );

  const handleEditProduct = () => {
    setEditProductOpen(true);
  };

  const handleAddSize = () => {
    setAddSizeOpen(true);
  };

  const handleAddSizeClose = () => {
    setAddSizeOpen(false);
  };

  const handleAddAnother = () => {
    // This will reopen the dialog after it closes
    setAddSizeOpen(true);
  };

  const handleEditSize = (sizeId) => {
    setEditingSizeId(sizeId);
    setEditSizeOpen(true);
  };

  const handleDeleteSize = (price) => {
    if (window.confirm(`Are you sure you want to remove the ${price.size_option_name} size?`)) {
      deleteSizeMutation.mutate(price.id);
    }
  };

  const handleDeleteProduct = () => {
    if (window.confirm(`Are you sure you want to delete "${product?.name}"? This will remove all sizes and cannot be undone.`)) {
      deleteProductMutation.mutate(productId);
    }
  };

  const handleEditProductClose = () => {
    setEditProductOpen(false);
  };

  const handleEditSizeClose = () => {
    setEditSizeOpen(false);
    setEditingSizeId(null);
  };

  const getImageUrlForPrice = (priceId) => {
    if (!productImages) return null;
    const image = productImages.find(img => img.product_size_price_id === priceId);
    return image?.url || null;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="product-details-overlay" onClick={onClose}>
        <div className="product-details-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="product-details-header">
            <div>
              <h2>{product?.name || 'Product Details'}</h2>
              <span className={`status-badge ${product?.is_active ? 'active' : 'inactive'}`}>
                {product?.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <button className="close-button" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="product-details-content">
            {loadingProduct || loadingPrices ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <>
                {/* Product Information */}
                <div className="details-section">
                  <div className="section-header">
                    <h3>Product Information</h3>
                    <button className="edit-btn" onClick={handleEditProduct}>
                      <Edit size={16} />
                      Edit Product
                    </button>
                  </div>

                  <div className="info-grid">
                    <div className="info-item">
                      <label>Name</label>
                      <p>{product?.name}</p>
                    </div>
                    {product?.category_name && (
                      <div className="info-item">
                        <label>Category</label>
                        <p>{product.category_name}</p>
                      </div>
                    )}
                    {product?.description && (
                      <div className="info-item full-width">
                        <label>Description</label>
                        <p>{product.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sizes & Pricing */}
                <div className="details-section">
                  <div className="section-header">
                    <h3>Sizes & Pricing</h3>
                    <Button size="small" onClick={handleAddSize}>
                      <Plus size={16} style={{ marginRight: '4px' }} />
                      Add Size
                    </Button>
                  </div>

                  {prices && prices.length > 0 ? (
                    <div className="sizes-grid">
                      {prices.map((price) => (
                        <div key={price.id} className="size-card">
                          <div className="size-card-header">
                            <div>
                              <h4>{price.size_option_name}</h4>
                              <p className="size-price">{formatCurrency(price.price)}</p>
                            </div>
                            <div className="size-actions">
                              <button
                                className="icon-btn"
                                onClick={() => handleEditSize(price.id)}
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="icon-btn danger"
                                onClick={() => handleDeleteSize(price)}
                                title="Remove"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {price.description && (
                            <p className="size-description">{price.description}</p>
                          )}

                          {getImageUrlForPrice(price.id) && (
                            <div className="size-image">
                              <img
                                src={getImageUrlForPrice(price.id)}
                                alt={`${product?.name} - ${price.size_option_name}`}
                              />
                            </div>
                          )}

                          <div className="size-meta">
                            <span className="meta-item">
                              Stock: {price.stock_quantity}
                            </span>
                            <span className={`meta-badge ${price.is_available ? 'available' : 'unavailable'}`}>
                              {price.is_available ? 'Available' : 'Unavailable'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-sizes">
                      <Package size={40} />
                      <p>No sizes added yet</p>
                      <Button size="small" onClick={handleAddSize}>
                        <Plus size={16} style={{ marginRight: '4px' }} />
                        Add First Size
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="product-details-footer">
            <Button variant="outline" onClick={handleDeleteProduct} className="delete-product-btn">
              <Trash2 size={16} style={{ marginRight: '4px' }} />
              Delete Product
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>

      <EditProductDialog
        isOpen={editProductOpen}
        onClose={handleEditProductClose}
        productId={productId}
      />

      <EditSizeDialog
        isOpen={editSizeOpen}
        onClose={handleEditSizeClose}
        productSizePriceId={editingSizeId}
      />

      <AddSizeDialog
        isOpen={addSizeOpen}
        onClose={handleAddSizeClose}
        productId={productId}
        onAddAnother={handleAddAnother}
      />
    </>
  );
};

export default ProductDetailsDialog;
